/*
 * FractalAI bilingual rollout header.
 *
 * 文件定位 / File Purpose
 * - 中文：保留 JSX 运行时入口，负责全局 dayjs 兼容、Ant Design Pro Layout 外壳、研究导航、右侧上下文栏和根 ErrorBoundary。
 * - English: Keeps the JSX runtime entry for global dayjs compatibility, Ant Design Pro Layout shell, research navigation, context rail, and root ErrorBoundary.
 *
 * 调用关系 / Call Flow
 * - 中文：由 Umi 运行时加载；需要与 app.ts 的公开/受控页面判断保持一致，避免构建入口差异造成登录页外壳不一致。
 * - English: Loaded by Umi runtime; must stay consistent with app.ts for public/protected page detection to avoid login-shell drift between build entries.
 *
 * 数据与业务边界 / Data and Business Boundaries
 * - 中文：仅运行在浏览器侧或 Umi 构建侧，不直接访问数据库、不写 SQL、不复制服务端分型核心算法。
 * - English: Runs only in the browser or Umi build layer; it does not access databases, write SQL, or copy server-side fractal-core algorithms.
 *
 * 上线注意 / Release Notes
 * - 中文：作为智能研究工作台前端上线清单的一部分，修改时必须保持路由、白名单登录、报告/策略入口和生产构建校验一致。
 * - English: Part of the intelligence-workbench frontend rollout scope; keep routes, whitelist login, report/strategy entry points, and production-build checks aligned.
 */

import dayjs from 'dayjs';
import '@/utils/dayjs-compat'; // 引入 day.js 兼容模块
import GlobalStyle from './GlobalStyle';
import ResearchWorkspaceGuide from '@/components/ResearchWorkspaceGuide';
import ResearchContextRail from '@/components/ResearchContextRail';
import {
  AUTO_RESEARCH_LANE,
  buildResearchDocumentTitle,
  buildResearchMenuData,
  resolveResearchRouteMeta,
} from '@/utils/researchNavigation';
import { isStrategySignalWorkspacePath } from '@/utils/researchWorkspaceTheme';

// 覆盖全局 moment 对象，强制所有第三方库使用修补后的 day.js
window.moment = dayjs;

// 最小化测试配置
export async function getInitialState() {
  return {
    test: "Hello World"
  };
}

export const request = {
  credentials: 'include',
};

import { Select } from 'antd';
import { ActiveTickerProvider } from '@/contexts/ActiveTickerContext';

const { Option } = Select;
const isIntelligenceLoginPath = (pathname = '') => pathname === '/intelligence/login';
const isPublicExperiencePath = (pathname = '') =>
  pathname === '/' ||
  pathname === '/home' ||
  pathname.startsWith('/foundations') ||
  isIntelligenceLoginPath(pathname);
const HIDDEN_CONTEXT_RAIL_LANES = new Set([
  '/market/overview',
  '/research/symbol',
  AUTO_RESEARCH_LANE,
]);

const getRuntimeLocation = (location = {}) => {
  const runtimeLocation = typeof window !== 'undefined' ? window.location : {};
  return {
    pathname: location?.pathname || runtimeLocation.pathname || '',
    search: location?.search || runtimeLocation.search || '',
  };
};

const shouldHideResearchContextRail = (location = {}) => {
  const { pathname, search } = getRuntimeLocation(location);
  const routeMeta = resolveResearchRouteMeta(pathname, search);
  if (isStrategySignalWorkspacePath(pathname)) {
    return true;
  }
  return routeMeta ? HIDDEN_CONTEXT_RAIL_LANES.has(routeMeta.activeLane) : true;
};

export const layout = () => {
  return {
    title: '分型系统',
    logo: '/branding/fractalai-mark.svg',
    siderWidth: 168,
    menu: {
      locale: false,
    },
    menuDataRender: buildResearchMenuData,
    menuRender: (props, defaultDom) =>
      isPublicExperiencePath(typeof window !== 'undefined' ? window.location.pathname : '')
        ? false
        : defaultDom,
    headerRender: (props, defaultDom) =>
      isPublicExperiencePath(typeof window !== 'undefined' ? window.location.pathname : '')
        ? false
        : defaultDom,
    rightContentRender: () =>
      isPublicExperiencePath(typeof window !== 'undefined' ? window.location.pathname : '') ? null : (
        <div style={{ paddingRight: 16 }}>
          <Select
            defaultValue="zh-CN"
            style={{ width: 120 }}
            onChange={(value) => {
              // 暂时移除语言切换功能，避免错误
              console.log('Language changed to:', value);
            }}
          >
            <Option value="zh-CN">中文</Option>
            <Option value="en-US">English</Option>
          </Select>
        </div>
      ),
    onPageChange: () => {
      if (typeof document !== 'undefined') {
        document.title = buildResearchDocumentTitle(window.location.pathname, window.location.search);
      }
    },
    childrenRender: (children, props) => {
      const { pathname } = getRuntimeLocation(props?.location);
      const isPublicExperience = isPublicExperiencePath(pathname);
      const hideContextRail = shouldHideResearchContextRail(props?.location);

      return (
        <>
          <GlobalStyle />
          {isPublicExperience ? (
            <div style={{ padding: '24px 24px 32px' }}>{children}</div>
          ) : (
            <>
              <ResearchWorkspaceGuide location={props.location} />
              <div
                className={`fractal-research-shell${hideContextRail ? ' fractal-research-shell-single' : ''}`}
                style={{
                  display: 'grid',
                  gridTemplateColumns: hideContextRail
                    ? 'minmax(0, 1fr)'
                    : 'minmax(0, 1fr) minmax(248px, 272px)',
                  gap: hideContextRail ? 0 : 12,
                  alignItems: 'start',
                }}
              >
                <div className="fractal-research-content" style={{ minWidth: 0 }}>
                  {children}
                </div>
                {hideContextRail ? null : (
                  <div className="fractal-research-rail">
                    <ResearchContextRail location={props.location} />
                  </div>
                )}
              </div>
            </>
          )}
        </>
      );
    },
  };
}

import ErrorBoundary from '@/components/ErrorBoundary';

export function rootContainer(container) {
  return (
    <ErrorBoundary>
      <ActiveTickerProvider>
        {container}
      </ActiveTickerProvider>
    </ErrorBoundary>
  );
}
